import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, MapPin, Trash2, TrendingUp, TrendingDown, DollarSign, Calendar, Download, FileSpreadsheet, FileText, Table2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_IMAGES = [
  "https://images.unsplash.com/photo-1642976975710-1d8890dbf5ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzcyNDc0OTA3fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1758983065414-0dcda592dcb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjb3p5JTIwYS1mcmFtZSUyMGNhYmluJTIwd29vZHMlMjBleHRlcmlvcnxlbnwwfHx8fDE3NzI0NzQ5MDh8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1711426793022-759f493357ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBiZWFjaCUyMGhvdXNlJTIwb2NlYW4lMjB2aWV3JTIwc3Vubnl8ZW58MHx8fHwxNzcyNDc0OTEwfDA&ixlib=rb-4.1.0&q=85",
];

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

export default function Properties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [propertyStats, setPropertyStats] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("all-time");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    image_url: "",
    currency: "INR",
  });

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
    fetchDashboardSummary();
  }, []);

  useEffect(() => {
    if (properties.length > 0) {
      fetchPropertyStats();
    }
  }, [selectedPeriod, properties]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`);
      if (response.data.monthly_data) {
        const months = response.data.monthly_data.map(m => ({
          value: m.month,
          label: new Date(m.month + '-01').toLocaleString('en-US', { month: 'long', year: 'numeric' })
        }));
        setAvailableMonths(months);
      }
    } catch (error) {
      console.error("Error fetching dashboard summary:", error);
    }
  };

  const fetchPropertyStats = async () => {
    try {
      const period = selectedPeriod === "all-time" ? "all-time" : selectedPeriod;
      const response = await axios.get(`${API}/properties/stats/all?period=${period}`);
      setPropertyStats(response.data);
    } catch (error) {
      console.error("Error fetching property stats:", error);
    }
  };

  const getPropertyStats = (propertyId) => {
    return propertyStats.find(s => s.property_id === propertyId);
  };

  const getPeriodLabel = () => {
    if (selectedPeriod === "all-time") return "All Time";
    const month = availableMonths.find(m => m.value === selectedPeriod);
    return month ? month.label : "All Time";
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const imageUrl = formData.image_url || DEFAULT_IMAGES[properties.length % DEFAULT_IMAGES.length];
      await axios.post(`${API}/properties`, {
        ...formData,
        image_url: imageUrl,
      });
      toast.success("Property added successfully!");
      setDialogOpen(false);
      setFormData({ name: "", address: "", image_url: "", currency: "INR" });
      fetchProperties();
    } catch (error) {
      console.error("Error creating property:", error);
      toast.error("Failed to add property");
    }
  };

  const handleDeleteClick = (property, e) => {
    e.stopPropagation();
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete) return;
    
    try {
      await axios.delete(`${API}/properties/${propertyToDelete.id}`);
      toast.success(`Property "${propertyToDelete.name}" deleted successfully!`);
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
      fetchProperties();
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
  };

  const handleExport = async (property, format, e) => {
    if (e) e.stopPropagation();
    
    const formatConfig = {
      csv: { endpoint: 'csv', extension: 'csv', name: 'CSV' },
      excel: { endpoint: 'excel', extension: 'xlsx', name: 'Excel' },
      pdf: { endpoint: 'pdf', extension: 'pdf', name: 'PDF' }
    };
    
    const config = formatConfig[format];
    if (!config) return;
    
    try {
      const response = await axios.get(`${API}/export/${config.endpoint}?property_id=${property.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transactions_${property.name.replace(/\s+/g, '_')}.${config.extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported ${config.name} for "${property.name}"`);
    } catch (error) {
      console.error("Error exporting transactions:", error);
      toast.error("Failed to export transactions");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="properties-loading">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-in" data-testid="properties-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="properties-title">
            Properties
          </h1>
          <p className="text-base text-muted-foreground mt-2">Manage your Airbnb properties</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
              data-testid="add-property-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="add-property-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold">Add New Property</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Enter the details of your Airbnb property to start tracking income and expenses.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Property Name</Label>
                <Input
                  id="name"
                  data-testid="property-name-input"
                  placeholder="e.g., Downtown Apartment"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="property-address-input"
                  placeholder="e.g., 123 Main St, City, State"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                  <SelectTrigger className="mt-1" data-testid="property-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code} data-testid={`currency-${currency.code}`}>
                        {currency.symbol} {currency.code} - {currency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="image_url">Image URL (Optional)</Label>
                <Input
                  id="image_url"
                  data-testid="property-image-input"
                  placeholder="https://example.com/image.jpg"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty for default image</p>
              </div>
              <Button
                type="submit"
                className="w-full rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-property-button"
              >
                Add Property
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Period Selector */}
      {properties.length > 0 && (
        <div className="mb-6">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-medium text-primary">
                    Showing data for: <span className="font-bold">{getPeriodLabel()}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="px-3 py-1.5 text-sm">
                    {selectedPeriod === "all-time" ? "Cumulative View" : "Monthly View"}
                  </Badge>
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger className="w-[200px]" data-testid="properties-period-selector">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-time" data-testid="properties-period-all-time">
                        All Time (Cumulative)
                      </SelectItem>
                      {availableMonths.map(month => (
                        <SelectItem key={month.value} value={month.value} data-testid={`properties-period-${month.value}`}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {properties.length === 0 ? (
        <Card className="bg-white rounded-xl border border-border/60 shadow-sm p-12 text-center" data-testid="no-properties">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No properties yet</h3>
          <p className="text-muted-foreground mb-4">Add your first property to start tracking income and expenses</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => {
            const stats = getPropertyStats(property.id);
            return (
              <Card
                key={property.id}
                className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                data-testid={`property-card-${property.id}`}
              >
                <div className="relative h-40 overflow-hidden cursor-pointer" onClick={() => navigate(`/properties/${property.id}`)}>
                  <img
                    src={property.image_url}
                    alt={property.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    onError={(e) => {
                      e.target.src = DEFAULT_IMAGES[0];
                    }}
                  />
                </div>
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-semibold text-primary cursor-pointer" onClick={() => navigate(`/properties/${property.id}`)}>{property.name}</CardTitle>
                    <div className="flex items-center gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                            onClick={(e) => e.stopPropagation()}
                            title="Export"
                            data-testid={`export-property-${property.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={(e) => handleExport(property, 'csv', e)} data-testid={`export-csv-${property.id}`}>
                            <Table2 className="w-4 h-4 mr-2 text-emerald-600" />
                            CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleExport(property, 'excel', e)} data-testid={`export-excel-${property.id}`}>
                            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                            Excel
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleExport(property, 'pdf', e)} data-testid={`export-pdf-${property.id}`}>
                            <FileText className="w-4 h-4 mr-2 text-red-600" />
                            PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
                        onClick={(e) => handleDeleteClick(property, e)}
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{property.address}</span>
                  </div>
                  
                  {/* Financial Summary */}
                  {stats && (
                    <div className="pt-3 border-t border-border/50 space-y-2" data-testid={`property-stats-${property.id}`}>
                      {/* Income */}
                      <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-900">Income</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-700" data-testid={`property-income-${property.id}`}>
                          {formatCurrency(stats.monthly_income)}
                        </span>
                      </div>
                      
                      {/* Expenses */}
                      <div className="flex items-center justify-between p-2 bg-rose-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="w-3.5 h-3.5 text-rose-600" />
                          <span className="text-xs font-medium text-rose-900">Expenses</span>
                        </div>
                        <span className="text-sm font-bold text-rose-700" data-testid={`property-expense-${property.id}`}>
                          {formatCurrency(stats.monthly_expense)}
                        </span>
                      </div>
                      
                      {/* Net Income */}
                      <div className={`flex items-center justify-between p-2 rounded-lg ${
                        stats.monthly_net >= 0 
                          ? 'bg-primary/5 border border-primary/20' 
                          : 'bg-rose-50 border border-rose-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-3.5 h-3.5 text-primary" />
                          <span className="text-xs font-medium text-primary">Net Income</span>
                        </div>
                        <span className={`text-sm font-bold ${
                          stats.monthly_net >= 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`} data-testid={`property-net-${property.id}`}>
                          {formatCurrency(stats.monthly_net)}
                        </span>
                      </div>
                      
                      {/* Profitability Badge */}
                      <Badge 
                        className={`w-full justify-center py-1 text-xs ${
                          stats.monthly_net >= 0
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-rose-100 text-rose-700 border-rose-200'
                        }`}
                        data-testid={`property-badge-${property.id}`}
                      >
                        {stats.monthly_net >= 0 ? '✓ Profitable' : '⚠ Loss'}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-property-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold">Delete Property?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Are you sure you want to delete <strong>{propertyToDelete?.name}</strong>? This will permanently delete the property and all associated transactions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete Property
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}